import { createApi } from '@reduxjs/toolkit/query/react';
import { DEEP_ANALYSIS_API_URL } from '../settings/config';
import { authRequest } from '../store/authorizedRequests';
import { DomainContentAnalysis, DomainVisibilityAnalysis, DomainContentAnalysisProcess, DomainVisibilityAnalysisProcess } from './types';

const llmToolsBaseUrl = `${DEEP_ANALYSIS_API_URL}`;

const RESOURCE_REFETCH_TIME = 7200;

export const llmToolsApi = createApi({
    reducerPath: "api.llm-tools",
    keepUnusedDataFor: RESOURCE_REFETCH_TIME,
    tagTypes: [
        "DomainContentAnalysis",
        "DomainVisibilityAnalysis",
        "ActiveProcesses",
    ],

    baseQuery: async (args, api, extraOptions) => {
        const { url, method, body, signal } = args;
        const { dispatch, getState } = api;

        try {
            const data = await authRequest(llmToolsBaseUrl.concat(url), dispatch, getState, {
                method: method,
                body: JSON.stringify(body),
                signal: signal,
            });

            return { data };
        } catch (error) {
            return { error };
        }
    },

    endpoints: (builder) => ({
        fetchDomainContentAnalysis: builder.query<DomainContentAnalysis, { domain: string }>({
            query: (args) => {
                const { domain } = args;

                return {
                    method: 'GET',
                    url: `/content-analysis?domain=${encodeURIComponent(domain)}`,
                };
            },
            providesTags: ['DomainContentAnalysis'],
        }),

        createDomainContentAnalysisProcess: builder.mutation<{ guid: string }, { domain: string }>({
            query: (args) => {
                return {
                    method: 'POST',
                    body: args,
                    url: '/content-analysis',
                };
            },

            invalidatesTags: (result, error, args) => [{ type: 'ActiveProcesses', id: `${args.domain}_domain_content_analysis` }],
        }),

        fetchDomainContentAnalysisProcess: builder.query<DomainContentAnalysisProcess, { domain: string, processGuid: string | null }>({
            query: (args) => {
                const { domain, processGuid } = args;
                const endpoint = processGuid ? `/content-analysis/active?domain=${encodeURIComponent(domain)}&process_guid=${processGuid}` : `/content-analysis/active?domain=${encodeURIComponent(domain)}`;
                return {
                    method: 'GET',
                    url: endpoint,
                };
            },

            providesTags: (result, error, args) => [{ type: 'ActiveProcesses', id: `${args.domain}_domain_content_analysis` }],
        }),

        fetchDomainVisibilityAnalysis: builder.query<DomainVisibilityAnalysis, { domain: string }>({
            query: (args) => {
                const { domain } = args;

                return {
                    method: 'GET',
                    url: `/visibility-analysis?domain=${encodeURIComponent(domain)}`,
                };
            },
            providesTags: ['DomainVisibilityAnalysis']
        }),

        createDomainVisibilityAnalysisProcess: builder.mutation<{ guid: string }, { domain: string }>({
            query: (args) => {
                return {
                    method: 'POST',
                    body: args,
                    url: '/visibility-analysis',
                };
            },

            invalidatesTags: (result, error, args) => [{ type: 'ActiveProcesses', id: `${args.domain}_domain_visibility_analysis` }],
        }),

        fetchDomainVisibilityAnalysisProcess: builder.query<DomainVisibilityAnalysisProcess, { domain: string, processGuid: string | null }>({
            query: (args) => {
                const { domain, processGuid } = args;
                const endpoint = processGuid ? `/visibility-analysis/active?domain=${encodeURIComponent(domain)}&process_guid=${processGuid}` : `/visibility-analysis/active?domain=${encodeURIComponent(domain)}`;
                return {
                    method: 'GET',
                    url: endpoint,
                };
            },

            providesTags: (result, error, args) => [{ type: 'ActiveProcesses', id: `${args.domain}_domain_visibility_analysis` }],
        }),

    })
});

export const {
    useFetchDomainContentAnalysisQuery,
    useLazyFetchDomainContentAnalysisQuery,
    useFetchDomainContentAnalysisProcessQuery,
    useCreateDomainContentAnalysisProcessMutation,

    useFetchDomainVisibilityAnalysisQuery,
    useLazyFetchDomainVisibilityAnalysisQuery,
    useFetchDomainVisibilityAnalysisProcessQuery,
    useCreateDomainVisibilityAnalysisProcessMutation,
} = llmToolsApi;

